import * as React from "react";
import { observer } from "mobx-react-lite";
import {
  Button,
  Collapse,
  Flex,
  Form,
  notification,
  Space,
  Tooltip,
} from "antd";
import { DeleteOutlined, QuestionCircleOutlined } from "@ant-design/icons";
import { t } from "i18next";
import { AttrTypeName, isAttrDictionary } from "src/types/AttrType";
import { findNode } from "src/common/findNode";
import { ZAttribute } from "src/types/ZAttribute";
import { isIteratorAvailable } from "src/pages/ManagementPage/objIterator";
import { VerticalContent } from "src/components/VerticalContent";
import { FieldsWithTitle } from "src/pages/ManagementPage/FieldsWithTitle";
import { WidthLimitedFields } from "src/pages/ManagementPage/WidthLimitedFields";
import { loadObjectAttrinbutesAll } from "src/pages/ManagementPage/objectsApi";
import { onError } from "src/common/onError";
import { SwitchFormItem } from "src/components/Forms/SwitchFormItem";
import { ZUid } from "src/types/zUid";
import {
  CurrentNodePort,
  ReferencesPort,
  TreeActionsPort,
  TreeDataPort,
} from "src/pages/ManagementPage/objects/ports";
import { SelectAttribute, buildTreeForSrcDict } from "../SelectAttribute";
import { edAttrField } from "../../EdAttribute";
import { findNodeOwnerObject } from "../../utils/findNodeOwnerObject";
import { AttributeO2, CommonNodeO2, newItemId } from "../../Obj2Nodes";
import { RestrictionsTableField } from "./RestrictionsTableField";
import { RolesEditor } from "../../roles/RolesEditor";
import { UpToObject } from "../UpToObject";
import { AttrCommonFields } from "./AttrCommonFields";
import styles from "./AttrForm2.module.less";
import { CopyOnStatesButton } from "../../roles/CopyOnStatesButton";
import { PermissionsCopyType } from "../../roles/roleTypes";
import { CopyOnAttsButton } from "../../roles/CopyOnAttsButton";
import { SelectFilter } from "./SelectFilter";
import { SelectBacklinkAttribute } from "./SelectBacklinkAttribute";
import { CopyOnMnemonicGroupButton } from "../../roles/CopyOnMnemonicGroupButton";
import { findNodeOwnerMnemonicGroup } from "../../utils/findNodeOwnerMnemonicGroup";

/**
 * Внутри формы формат полей соответствует EdAttribute
 */

interface PropsAttrForm2 {
  store: CurrentNodePort & ReferencesPort & TreeActionsPort & TreeDataPort;
  attrNode: AttributeO2;
}

export const AttrForm2: React.FC<PropsAttrForm2> = observer(
  ({ store, attrNode }) => {
    const { attr, states = [] } = attrNode;
    const valueType = Form.useWatch(edAttrField.valueType);
    const typeName: AttrTypeName = store.attrTypesDict[
      valueType
    ] as AttrTypeName;
    const objIteratorMode = isIteratorAvailable(
      attrNode,
      typeName,
      store.treeData,
    );
    const useRestriction = Form.useWatch(edAttrField.useRestriction);
    const disabled = !attrNode.actions.update;
    const restrChangeDisabled = !!attrNode.attr.parentAttributeId;

    const typeFilter = (typeId: number): boolean =>
      isAttrDictionary(store.attrTypesDict[typeId]);
    const dictVisible = typeFilter(valueType);

    // Доступ к словарям
    const [refTree, setRefTree] = React.useState<CommonNodeO2[]>([]);
    React.useEffect(() => {
      if (dictVisible) {
        const owner = findNodeOwnerObject(attrNode.key, store.treeData);
        if (owner) {
          setRefTree(
            buildTreeForSrcDict(owner, { dstAttrId: attr.id, typeFilter }),
          );
        } else notification.warning({ message: "Не найден объект" });
      } else {
        setRefTree([]);
      }
    }, [dictVisible]);

    const ownerMnemonicGroup = React.useMemo(
      () => findNodeOwnerMnemonicGroup(attrNode.key, store.treeData),
      [attrNode.key],
    );

    const rolesGroup = store.roleGroups;

    const getStateId = (index: number) => states[index]?.id.toString() ?? "";

    const canCopyPermissions = attrNode.attr.id !== newItemId;
    const objectName = store.curObjectName ?? "";
    const objectId = store.currentObjectId;

    const [neighborAttrs, setNeighborAttrs] = React.useState<ZUid[]>([]);
    React.useEffect(() => {
      if (objectId) {
        loadObjectAttrinbutesAll(objectId)
          .then((attrs) =>
            setNeighborAttrs(
              attrs.filter(({ id }) => id !== attr.id).map(({ id }) => id),
            ),
          )
          .catch(onError);
      }
    }, [objectId]);

    const isObjectRef = typeName === AttrTypeName.object;
    const extraSettingsVisible = dictVisible || isObjectRef;
    const form = Form.useFormInstance();
    const referenceId = Form.useWatch(edAttrField.referenceId);
    /**
     * backlinkAttributeId указывает на атрибут выбранного объекта, поэтому при смене
     * объекта значение нужно сбросить.
     * Сравниваем с сохранённым attr.referenceId, а не с предыдущим значением поля:
     * форма живёт дольше, чем выбранный узел дерева (ModelessForm просто перезаписывает
     * значения), и «предыдущее» значение относилось бы к другому атрибуту.
     */
    React.useEffect(() => {
      if ((referenceId ?? null) === (attr.referenceId ?? null)) return;
      form.setFieldValue(edAttrField.backlinkAttributeId, null);
    }, [form, referenceId, attr.referenceId]);

    if (!objectId) return null;

    return (
      <>
        <AttrCommonFields
          attrKey={attrNode.key}
          attrId={attrNode.attr.id}
          attrDisplayId={attrNode.attr.displayId || ""}
          objectId={objectId}
          disabled={disabled}
          dataSource={store}
          isFieldUnique={uniqueAttrTest(store.treeData)}
          loadObjectOptions={() => store.loadObjectOptions()}
          objIteratorMode={objIteratorMode}
          systemAttrType={attr.systemAttributeType}
        />

        <WidthLimitedFields>
          {/* Переключатель становится виден только если тип текущего атрибута использует словарь */}
          {extraSettingsVisible && (
            <FieldsWithTitle title="Дополнительные настройки">
              <VerticalContent>
                {dictVisible && (
                  <>
                    <SwitchFormItem
                      name={edAttrField.useRestriction}
                      text="Ограничения по значению"
                      disabled={disabled || restrChangeDisabled}
                    />
                    {useRestriction && (
                      <>
                        <Form.Item
                          name={edAttrField.masterAttrId}
                          label="Выберите атрибут"
                          rules={[
                            {
                              required: true,
                              message: "Необходимо выбрать атрибут",
                            },
                          ]}
                          className="idMasterAttr"
                        >
                          <SelectAttribute
                            tree={refTree}
                            disabled={disabled || restrChangeDisabled}
                          />
                        </Form.Item>
                        <RestrictionsTableField
                          disabled={disabled || restrChangeDisabled}
                        />
                      </>
                    )}
                  </>
                )}

                {isObjectRef && (
                  <VerticalContent>
                    <div>{t("Limit instance list selection:")}</div>
                    <Form.Item
                      name={edAttrField.filter}
                      label={
                        <LabelWithHint
                          text={t("By another attribute value")}
                          hint={t("By another attribute value tooltip")}
                        />
                      }
                    >
                      <SelectFilter
                        attributeId={attr.id}
                        disabled={disabled}
                        placeholder={t("Select")}
                      />
                    </Form.Item>
                    <Form.Item
                      name={edAttrField.backlinkAttributeId}
                      label={
                        <LabelWithHint
                          text={t("By references (referencing instances only)")}
                          hint={t("By references tooltip")}
                        />
                      }
                    >
                      <SelectBacklinkAttribute
                        targetObjectId={referenceId}
                        currentObjectId={objectId}
                        attrTypesDict={store.attrTypesDict}
                        excludeAttrId={attr.id}
                        disabled={disabled}
                        placeholder={t("Select")}
                      />
                    </Form.Item>
                  </VerticalContent>
                )}
              </VerticalContent>
            </FieldsWithTitle>
          )}

          <FieldsWithTitle title="Права">
            <Collapse
              accordion
              defaultActiveKey={getStateId(0)}
              items={states.map(({ id, name, displayId }) => ({
                key: String(id),
                label: `${name} (${displayId ?? id})`,
                forceRender: true,
                children: (
                  <Form.Item label="" name={[edAttrField.rolesMap, String(id)]}>
                    <RolesEditor
                      columns={store.attrTypeActions}
                      rows={rolesGroup}
                      canCopy={canCopyPermissions}
                      stateId={id}
                      store={store}
                    />
                  </Form.Item>
                ),
                extra: (
                  <Flex align="center" gap={12}>
                    {canCopyPermissions && (
                      <>
                        <CopyOnStatesButton
                          stateName={name}
                          stateId={id}
                          copyType={PermissionsCopyType.attribute}
                          store={store}
                        />
                        <CopyOnAttsButton
                          stateName={name}
                          stateId={id}
                          objectName={objectName}
                          attributeIds={neighborAttrs}
                        />
                      </>
                    )}
                    <Form.Item
                      name={edAttrField.copyStatesForMnemonicGroup}
                      noStyle
                    >
                      <CopyOnMnemonicGroupButton
                        store={store}
                        stateName={name}
                        stateId={id}
                        owner={ownerMnemonicGroup}
                      />
                    </Form.Item>
                  </Flex>
                ),
              }))}
            />
          </FieldsWithTitle>
        </WidthLimitedFields>
      </>
    );
  },
);

const LabelWithHint: React.FC<{ text: string; hint: string }> = ({
  text,
  hint,
}) => (
  <Space size={4}>
    {text}
    <Tooltip title={hint}>
      <QuestionCircleOutlined />
    </Tooltip>
  </Space>
);

const uniqueAttrTest =
  (treeData: CommonNodeO2[]) =>
  (
    field: keyof ZAttribute,
    attrKey: React.Key,
    attrId: ZUid,
    value: unknown,
  ): boolean => {
    const obj = findNodeOwnerObject(attrKey, treeData);
    return !findNode((node: CommonNodeO2) => {
      if (node.type !== "attr" || node.attr.id === attrId || !value)
        return false;
      return node.attr[field] === value;
    }, obj?.children ?? []);
  };

export const AttrFormButtons: React.FC<PropsAttrForm2> = observer(
  ({ store, attrNode }) => (
    <div className={styles.buttons}>
      <UpToObject store={store} />
      <Button
        danger
        icon={<DeleteOutlined />}
        onClick={() => store.safeDeleteCurNode()}
        disabled={!attrNode.actions.delete}
        id="deleteCurAttr"
      >
        Удалить атрибут
      </Button>
    </div>
  ),
);
